import {
  IOrderOperationDuration,
  IOrderOperationTimingPhase,
} from "../../models/order/response";

const formatDurationPart = (value: number, label: string) => `${value} ${label}`;

interface IOperationDurationLabels {
  minuteLabel: string;
  secondLabel: string;
  hourLabel: string;
  dayLabel: string;
}

export interface IOperationDurationPart {
  key: "day" | "hour" | "minute" | "second";
  label: string;
  value: number;
}

export const hasOperationTimingNumber = (
  value?: number | null,
): value is number => typeof value === "number" && Number.isFinite(value);

export const formatOperationSeconds = (
  value?: number | null,
  secondLabel = "sec",
) => {
  if (!hasOperationTimingNumber(value)) return "-";
  const formatted = Number.isInteger(value) ? `${value}` : value.toFixed(1);
  return `${formatted} ${secondLabel}`;
};

export const durationFromSeconds = (
  value?: number | null,
): IOrderOperationDuration | null => {
  if (!hasOperationTimingNumber(value)) return null;

  const totalSeconds = Math.max(0, Math.trunc(value));
  return {
    totalSeconds,
    minutes: Math.floor(totalSeconds / 60),
    seconds: totalSeconds % 60,
  };
};

export const formatOperationDuration = (
  duration?: IOrderOperationDuration | null,
  fallbackSeconds?: number | null,
  minuteLabel = "min",
  secondLabel = "sec",
  hourLabel = "h",
  dayLabel = "d",
) => {
  const resolvedDuration = duration ?? durationFromSeconds(fallbackSeconds);

  if (!resolvedDuration) return "-";

  const totalSeconds = Math.max(0, Math.trunc(resolvedDuration.totalSeconds));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(formatDurationPart(days, dayLabel));
  }

  if (hours > 0) {
    parts.push(formatDurationPart(hours, hourLabel));
  }

  if (minutes > 0) {
    parts.push(formatDurationPart(minutes, minuteLabel));
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(formatDurationPart(seconds, secondLabel));
  }

  return parts.join(" ");
};

export const getOperationDurationParts = (
  duration?: IOrderOperationDuration | null,
  fallbackSeconds?: number | null,
  labels: IOperationDurationLabels = {
    minuteLabel: "min",
    secondLabel: "sec",
    hourLabel: "h",
    dayLabel: "d",
  },
): IOperationDurationPart[] | null => {
  const resolvedDuration = duration ?? durationFromSeconds(fallbackSeconds);

  if (!resolvedDuration) return null;

  const totalSeconds = Math.max(0, Math.trunc(resolvedDuration.totalSeconds));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: IOperationDurationPart[] = [];

  if (days > 0) {
    parts.push({ key: "day", label: labels.dayLabel, value: days });
  }

  if (hours > 0) {
    parts.push({ key: "hour", label: labels.hourLabel, value: hours });
  }

  if (minutes > 0) {
    parts.push({ key: "minute", label: labels.minuteLabel, value: minutes });
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push({ key: "second", label: labels.secondLabel, value: seconds });
  }

  return parts;
};

export const getOperationPhaseDuration = (
  phase?: IOrderOperationTimingPhase | null,
) =>
  (phase?.isActive ? phase?.elapsedDuration : null) ??
  phase?.effectiveDuration ??
  phase?.duration ??
  phase?.elapsedDuration ??
  null;

export const getOperationPhaseSeconds = (
  phase?: IOrderOperationTimingPhase | null,
) =>
  (phase?.isActive ? phase?.elapsedSeconds : null) ??
  phase?.effectiveSeconds ??
  phase?.durationSeconds ??
  phase?.elapsedSeconds ??
  null;
